import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../../../contexts/AuthContext';
import userMappings from '../../../../../config/userMappings';
import {
  Box,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Flex
} from '@chakra-ui/react';

// Import modular components
import {
  ChatHeader,
  ChatMessages,
  ChatInput,
  PresetMessages,
  formatPhoneForWhatsApp,
  ChatService
} from './WhatsAppIntegration';

/**
 * Component for WhatsApp integration to support direct messaging and group access
 * Enhanced with chat history and messaging functionality
 * Uses modular components for better organization and maintainability
 */
const NewKBWhatsAppIntegration = ({ phoneNumber, whatsappGroup, whatsappGroupName }) => {
  // Core color values
  const bgColor = useColorModeValue('green.50', 'green.900');
  const borderColor = useColorModeValue('green.200', 'green.700');
  
  // Chat state
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [chatTarget, setChatTarget] = useState(whatsappGroup?.includes('@g.us') ? 'group' : 'personal'); // Auto-detect group when available
  const [oldestTimestamp, setOldestTimestamp] = useState(null);
  const [hasLoadedEarlierMessages, setHasLoadedEarlierMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const toast = useToast();
  const { user } = useAuth();
    const getSenderName = (uid) => {
    if (!uid) return 'Sumaaroh';
    const mappedUser = userMappings.users.find(u => u.uid === uid);
    return mappedUser?.displayName || user?.displayName || 'Sumaaroh';
  };
  const senderName = getSenderName(user?.uid);
  
  // Format the phone number for WhatsApp
  const formattedPhone = formatPhoneForWhatsApp(phoneNumber);
  
  // Helper function to show toast notifications
  const showToast = (title, description, status = 'info') => {
    // Return void to ensure we're not returning the toast object
    void toast({
      title,
      description,
      status,
      duration: 3000,
      isClosable: true,
      position: 'top-right'
    });
  };
  
  // Load more messages when scrolling to top
  const loadMoreMessages = () => {
    // Skip if already loading, no history, or already loaded earlier messages
    if (isLoadingMore || !chatHistory.length || hasLoadedEarlierMessages) {
      console.log('[WhatsAppIntegration] Skipping load more messages:', 
        isLoadingMore ? 'already loading' : 
        !chatHistory.length ? 'no history' : 
        'earlier messages already loaded');
      
      // Make sure to reset loading state if we're skipping
      setIsLoadingMore(false);
      return;
    }
    
    // Find the oldest message in the current history
    const oldestMessage = chatHistory[0]; // Assuming messages are sorted chronologically (oldest first)
    const timestamp = oldestMessage.timestamp || 
                     (oldestMessage._data && oldestMessage._data.t) || 
                     null;
    
    if (timestamp) {
      console.log('[WhatsAppIntegration] Loading more messages before timestamp:', timestamp);
      setOldestTimestamp(timestamp);
      
      // Set flag for tracking that we've loaded earlier messages
      setHasLoadedEarlierMessages(true);
      
      fetchChatHistory(true);
    } else {
      console.log('[WhatsAppIntegration] No valid timestamp found for loading more messages');
      setIsLoadingMore(false);
    }
  };
  
  // Fetch chat history with error handling
  const fetchChatHistory = async (loadingMore = false) => {
    // Validate necessary data is available
    if ((!formattedPhone || formattedPhone.length < 5) && chatTarget === 'personal') {
      setError('Valid phone number required for personal chat');
      return;
    }
    if ((!whatsappGroup || !whatsappGroup.includes('@g.us')) && chatTarget === 'group') {
      setError('Valid WhatsApp group ID required');
      return;
    }

    // Set loading state based on operation type
    if (loadingMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      // Only clear chat history on fresh load, not when loading more
      setChatHistory([]);
      // Reset oldest timestamp when doing a fresh load
      setOldestTimestamp(null);
      // Reset the early load tracking when starting fresh
      setHasLoadedEarlierMessages(false);
    }
    setError(null);
    
    // Get the correct chat ID based on selected chat type
    const chatId = chatTarget === 'group' ? whatsappGroup : formattedPhone;
    
    // Use the ChatService to fetch messages
    await ChatService.fetchMessages(
      chatId, 
      10, 
      loadingMore ? setIsLoadingMore : setIsLoading,
      setError,
      setChatHistory,
      showToast,
      loadingMore,
      loadingMore ? oldestTimestamp : null
    );
  };
  
  // Scroll to bottom of chat
  const scrollToBottom = (instant = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: instant ? "instant" : "smooth" });
  };
  
  // Handle chat scroll
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    
    // Check if we're near the bottom for the scroll button
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollButton(!isNearBottom);
    
    // Only check for top scroll if we're not already loading more messages and haven't loaded them yet
    if (scrollTop < 30 && !isLoadingMore && !hasLoadedEarlierMessages && chatHistory.length >= 10) {
      console.log('[WhatsAppIntegration] Near top of chat, loading more messages');
      // Set loading flag immediately to prevent multiple triggers
      setIsLoadingMore(true);
      loadMoreMessages();
    }
  };
  
  // Send a message
  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    // Validate we have required info for the selected chat type
    if (chatTarget === 'personal' && !formattedPhone) {
      void showToast('Error', 'No phone number available for personal chat', 'error');
      return;
    } else if (chatTarget === 'group' && !whatsappGroup) {
      void showToast('Error', 'No group chat available', 'error');
      return;
    }
    
    const chatId = chatTarget === 'group' ? whatsappGroup : formattedPhone;
    setIsSending(true);
    
    // Use the ChatService to send the message
    await ChatService.sendMessage(
      chatId,
      message,
      senderName,
      setIsSending,
      showToast,
      () => setMessage(''),
      async () => {
        await fetchChatHistory(true);
        scrollToBottom();
      }
    );
  };
  
  // Send all introduction messages in sequence
  const handleSendIntroMessages = async () => {
    // Only allow intro sequence for personal chats
    if (chatTarget !== 'personal') {
      void showToast('Error', 'Introduction sequence is only available for personal chats', 'warning');
      return;
    }
    
    if (!formattedPhone || isSending) {
      void showToast('Error', 'No phone number available for personal chat', 'error');
      return;
    }
    
    setIsSending(true);
    
    // Use the ChatService to send the intro messages
    await ChatService.sendIntroMessages(
      formattedPhone,
      senderName,
      setIsSending,
      showToast,
      async () => {
        await fetchChatHistory(true);
        scrollToBottom();
      }
    );
  };
  
  // Effects to manage chat
  useEffect(() => {
    if (formattedPhone && chatTarget === 'personal') {
      // Reset the early load tracking when changing chat targets
      setHasLoadedEarlierMessages(false);
      fetchChatHistory();
    }
  }, [formattedPhone, chatTarget]);

  useEffect(() => {
    if (whatsappGroup && chatTarget === 'group') {
      // Reset the early load tracking when changing chat targets
      setHasLoadedEarlierMessages(false);
      fetchChatHistory();
    }
  }, [whatsappGroup, chatTarget]);

  useEffect(() => {
    // Only auto-scroll to bottom on initial load or for refreshed chats
    // Do not auto-scroll when loading more (older) messages
    if (chatHistory.length > 0 && !isLoadingMore) {
      // Use instant scroll on initial load, smooth scroll for updates
      scrollToBottom(true);
    }
  }, [chatHistory, isLoadingMore]);
  
  return (
    <Box 
      h="100%" 
      display="flex"
      flexDirection="column"
      borderWidth="1px" 
      borderRadius="md" 
      bg={bgColor}
      borderColor={borderColor}
      position="relative"
    >
      {/* Header Component */}
                  <ChatHeader 
        chatTarget={chatTarget}
        setChatTarget={setChatTarget}
        whatsappGroupName={whatsappGroupName}
        formattedPhone={formattedPhone}
        whatsappGroup={whatsappGroup}
        fetchChatHistory={fetchChatHistory}
        isRefreshing={isLoadingMore}
      />

      {/* No contact info alert */}
      {!formattedPhone && !whatsappGroup ? (
        <Alert status="info" borderRadius="md" fontSize="sm" m={4}>
          <AlertIcon />
          No WhatsApp contact info available
        </Alert>
      ) : (
        <>
          {/* Chat Messages Component */}
          <ChatMessages 
            chatHistory={chatHistory}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            error={error}
            messagesEndRef={messagesEndRef}
            chatContainerRef={chatContainerRef}
            handleScroll={handleScroll}
          />

          {/* Preset Messages Component */}
          <Flex px={3} flexWrap="wrap">
            <PresetMessages
              setMessage={setMessage}
              handleSendIntroMessage={handleSendIntroMessages}
              isSending={isSending}
              chatTarget={chatTarget}
              hasMessages={Array.isArray(chatHistory) && chatHistory.length > 0}
            />
          </Flex>

          {/* Message Input Component */}
          <ChatInput 
            senderName={senderName}
            chatTarget={chatTarget}
            message={message}
            setMessage={setMessage}
            handleSendMessage={handleSendMessage}
            isSending={isSending}
            borderColor={borderColor}
          />
        </>
      )}
    </Box>
  );
};

export default NewKBWhatsAppIntegration;
